// ============================================================================
// GESTIMMONY — 20 modèles de courriers PDF pour bailleurs
// ============================================================================
// Galerie par catégorie → click modèle → formulaire dynamique (champs
// définis côté backend) → génération PDF téléchargée.
// ============================================================================
import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { planContext, handleGateError } from "@/lib/plan";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  FileText, AlertTriangle, Home, Calculator, Receipt, Wrench,
  ScrollText, Download, Search, Mail, Lock,
} from "lucide-react";

const TIER_RANK = { essentiel: 1, patrimoine: 2, prestige: 3 };
const TIER_LABEL = { essentiel: "Essentiel", patrimoine: "Patrimoine", prestige: "Prestige" };

const CAT_META = {
  "Impayés":       { icon: AlertTriangle, color: "bg-rose-100 text-rose-700 border-rose-200" },
  "Bail":          { icon: Home,          color: "bg-teal-100 text-teal-700 border-teal-200" },
  "Loyer":         { icon: Calculator,    color: "bg-amber-100 text-amber-700 border-amber-200" },
  "État des lieux":{ icon: Receipt,       color: "bg-indigo-100 text-indigo-700 border-indigo-200" },
  "Fiscal":        { icon: ScrollText,    color: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  "Administratif": { icon: Wrench,        color: "bg-slate-100 text-slate-700 border-slate-200" },
};

export default function LettersPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { tier, limits } = planContext(user);
  const isAdmin = user?.role === "admin";
  const userRank = isAdmin ? 99 : TIER_RANK[tier || "essentiel"];
  const monthlyLimit = isAdmin ? null : (limits?.letters ?? 8);
  const [templates, setTemplates] = useState([]);
  const [monthCount, setMonthCount] = useState(0);
  const [selected, setSelected] = useState(null);   // template détaillé (avec fields)
  const [values, setValues] = useState({});
  const [generating, setGenerating] = useState(false);
  const [sendingTenant, setSendingTenant] = useState(false);
  const [category, setCategory] = useState("Tous");
  const [query, setQuery] = useState("");
  const [tenants, setTenants] = useState([]);
  const [properties, setProperties] = useState([]);
  const [leases, setLeases] = useState([]);
  const [pickTenant, setPickTenant] = useState("");
  const [pickProperty, setPickProperty] = useState("");

  useEffect(() => {
    api.get("/letters/templates").then(({ data }) => setTemplates(data.templates));
    // Charge locataires, biens et baux pour le pré-remplissage des courriers
    api.get("/tenants").then(({ data }) => setTenants(data)).catch(() => {});
    api.get("/properties").then(({ data }) => setProperties(data)).catch(() => {});
    api.get("/leases").then(({ data }) => setLeases(data)).catch(() => {});
    // Compte les courriers générés ce mois-ci (pour afficher le quota restant)
    api.get("/letters/history").then(({ data }) => {
      const start = new Date();
      start.setDate(1); start.setHours(0, 0, 0, 0);
      setMonthCount((data || []).filter((h) => new Date(h.created_at) >= start).length);
    }).catch(() => {});
  }, []);

  const isLocked = (t) => userRank < (TIER_RANK[t.min_tier] || 1);

  const categories = useMemo(() => {
    const set = new Set(templates.map((t) => t.category));
    return ["Tous", ...Array.from(set)];
  }, [templates]);

  const filtered = useMemo(() => {
    return templates.filter((t) => {
      if (category !== "Tous" && t.category !== category) return false;
      if (query) {
        const q = query.toLowerCase();
        return t.title.toLowerCase().includes(q) || t.description.toLowerCase().includes(q);
      }
      return true;
    });
  }, [templates, category, query]);

  const openTemplate = async (key) => {
    const meta = templates.find((t) => t.key === key);
    if (meta && isLocked(meta)) {
      toast.error(`Ce modèle est inclus dès l'offre ${TIER_LABEL[meta.min_tier]}`, {
        duration: 7000,
        action: { label: "Voir les offres", onClick: () => navigate("/app/abonnement") },
      });
      return;
    }
    try {
      const { data } = await api.get(`/letters/templates/${key}`);
      setSelected(data);
      const init = {};
      data.fields.forEach((f) => { init[f.name] = ""; });
      setValues(init);
      setPickTenant("");
      setPickProperty("");
    } catch { toast.error("Impossible de charger le modèle"); }
  };

  // Pré-remplissage intelligent : locataire + bien (+ bail lié si trouvé)
  const applyPicks = (tenantId, propertyId) => {
    const t = tenants.find((x) => x.id === tenantId);
    const lease = leases.find((l) =>
      (!tenantId || l.tenant_id === tenantId) && (!propertyId || l.property_id === propertyId));
    // Si un locataire est choisi sans bien explicite, on déduit le bien depuis son bail
    let p = properties.find((x) => x.id === propertyId);
    if (!p && lease) p = properties.find((x) => x.id === lease.property_id);
    setValues((prev) => {
      const v = { ...prev };
      if (t) {
        if ("tenant_name" in v) v.tenant_name = `${t.first_name || ""} ${t.last_name || ""}`.trim();
        if ("tenant_address" in v && p) v.tenant_address = `${p.address || ""}, ${p.postal_code || ""} ${p.city || ""}`.trim().replace(/^,\s*|,\s*$/g, "");
        if ("tenant_address" in v && !p) v.tenant_address = v.tenant_address || "";
        if ("tenant_birthdate" in v) v.tenant_birthdate = t.birth_date || "";
      }
      if (p) {
        const fullAddr = `${p.address || ""}, ${p.postal_code || ""} ${p.city || ""}`.trim().replace(/^,\s*|,\s*$/g, "");
        if ("property_address" in v) v.property_address = fullAddr;
        if ("property_name" in v) v.property_name = p.label || "";
        if ("tenant_address" in v && !t) v.tenant_address = fullAddr;
      }
      if (lease) {
        if ("amount" in v) v.amount = lease.rent_amount ?? "";
        if ("end_date" in v) v.end_date = lease.end_date ?? "";
        if ("since_date" in v) v.since_date = lease.start_date ?? "";
        if ("old_rent" in v) v.old_rent = lease.rent_amount ?? "";
        if ("rent_amount" in v) v.rent_amount = lease.rent_amount ?? "";
        if ("charges" in v) v.charges = lease.charges_amount ?? "";
        if ("deposit_amount" in v) v.deposit_amount = lease.deposit_amount ?? "";
        if ("period" in v && !v.period) {
          v.period = new Date().toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
        }
      }
      return v;
    });
  };

  const generate = async () => {
    if (!selected) return;
    const missing = selected.fields.filter((f) => f.required && !values[f.name]).map((f) => f.label);
    if (missing.length) { toast.error("Champs manquants : " + missing.join(", ")); return; }
    setGenerating(true);
    try {
      const res = await api.post(`/letters/${selected.key}/pdf`, { values },
                                 { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `gestimmony-${selected.key.replaceAll("_", "-")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("PDF généré et téléchargé");
      setSelected(null);
    } catch (e) { if (!handleGateError(e, navigate)) toast.error("Erreur génération : " + (e.response?.data?.detail || e.message)); }
    finally { setGenerating(false); }
  };

  const sendToTenant = async () => {
    if (!selected) return;
    if (!pickTenant) { toast.error("Choisissez d'abord le locataire concerné ci-dessus"); return; }
    const missing = selected.fields.filter((f) => f.required && !values[f.name]).map((f) => f.label);
    if (missing.length) { toast.error("Champs manquants : " + missing.join(", ")); return; }
    setSendingTenant(true);
    try {
      const { data } = await api.post(`/letters/${selected.key}/send-to-tenant`,
                                      { values, tenant_id: pickTenant });
      toast.success(data.email_sent
        ? "Courrier déposé dans l'espace locataire et notifié par e-mail ✉️"
        : data.tenant_email
          ? "Courrier déposé dans l'espace locataire (e-mail non délivré)"
          : "Courrier déposé dans l'espace locataire (aucun e-mail renseigné)");
      setSelected(null);
    } catch (e) { if (!handleGateError(e, navigate)) toast.error("Erreur envoi : " + (e.response?.data?.detail || e.message)); }
    finally { setSendingTenant(false); }
  };

  return (
    <div className="space-y-6" data-testid="letters-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900 tracking-tight">
            Modèles de courriers
          </h1>
          <p className="mt-1 text-slate-600 text-sm">
            20 modèles PDF prêts à envoyer, inspirés de la législation en vigueur et pré-remplis avec vos coordonnées.
          </p>
        </div>
        <Badge variant="outline" className="border-teal-500/40 text-teal-700 bg-teal-50" data-testid="letters-quota-badge">
          {monthlyLimit === null
            ? `${templates.length} modèles · admin`
            : `${monthCount}/${monthlyLimit} ce mois · offre ${TIER_LABEL[tier || "essentiel"]}`}
        </Badge>
      </div>

      {/* Filtres */}
      <Card className="p-4 flex flex-col md:flex-row gap-3 items-stretch md:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            data-testid="letters-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Rechercher un modèle…"
            className="pl-9"
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          {categories.map((c) => (
            <button
              key={c}
              data-testid={`letters-filter-${c}`}
              onClick={() => setCategory(c)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
                category === c
                  ? "bg-[hsl(var(--primary))] text-white border-transparent"
                  : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      </Card>

      {/* Galerie */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map((t) => {
          const meta = CAT_META[t.category] || CAT_META["Administratif"];
          const Icon = meta.icon;
          const locked = isLocked(t);
          return (
            <Card
              key={t.key}
              data-testid={`letter-${t.key}`}
              data-locked={locked ? "true" : "false"}
              onClick={() => openTemplate(t.key)}
              className={`p-5 transition group relative ${
                locked
                  ? "cursor-not-allowed opacity-60 bg-slate-50 border-dashed"
                  : "cursor-pointer hover:border-teal-500 hover:shadow-md"
              }`}
            >
              {locked && (
                <div className="absolute top-3 right-3 flex items-center gap-1 text-[10px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
                  <Lock className="h-3 w-3" /> {TIER_LABEL[t.min_tier]}
                </div>
              )}
              <div className="flex items-start gap-3">
                <div className={`h-10 w-10 rounded-lg flex items-center justify-center shrink-0 border ${meta.color}`}>
                  <Icon className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">
                    {t.category}
                  </div>
                  <h3 className="font-heading font-bold text-slate-900 mt-0.5 leading-tight">
                    {t.title}
                  </h3>
                  <p className="mt-1 text-xs text-slate-500 line-clamp-2">{t.description}</p>
                </div>
              </div>
              <div className={`mt-4 text-xs font-semibold flex items-center gap-1 transition ${
                locked ? "text-amber-700" : "text-teal-700 opacity-0 group-hover:opacity-100"
              }`}>
                {locked
                  ? <><Lock className="h-3 w-3" /> Passer à {TIER_LABEL[t.min_tier]} →</>
                  : <><FileText className="h-3 w-3" /> Rédiger →</>}
              </div>
            </Card>
          );
        })}
        {filtered.length === 0 && (
          <div className="col-span-full text-center py-12 text-slate-400 text-sm">
            Aucun modèle ne correspond
          </div>
        )}
      </div>

      {/* Modal formulaire */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="letter-dialog">
          {selected && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-2 text-xs font-semibold text-teal-700 mb-1">
                  {selected.category}
                </div>
                <DialogTitle className="font-heading">{selected.title}</DialogTitle>
                <DialogDescription>{selected.description}</DialogDescription>
              </DialogHeader>

              {/* Sélecteurs locataire / bien → pré-remplissage */}
              {(tenants.length > 0 || properties.length > 0) && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-3 rounded-lg bg-teal-50/60 border border-teal-200/60">
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-teal-800">Locataire concerné</Label>
                    <Select
                      value={pickTenant}
                      onValueChange={(val) => { const v = val === "__none" ? "" : val; setPickTenant(v); applyPicks(v, pickProperty); }}
                    >
                      <SelectTrigger className="mt-1 bg-white" data-testid="letter-pick-tenant">
                        <SelectValue placeholder="Choisir un locataire…" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none">Aucun</SelectItem>
                        {tenants.map((t) => (
                          <SelectItem key={t.id} value={t.id}>
                            {t.first_name} {t.last_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-teal-800">Bien concerné</Label>
                    <Select
                      value={pickProperty}
                      onValueChange={(val) => { const v = val === "__none" ? "" : val; setPickProperty(v); applyPicks(pickTenant, v); }}
                    >
                      <SelectTrigger className="mt-1 bg-white" data-testid="letter-pick-property">
                        <SelectValue placeholder="Choisir un bien…" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none">Aucun</SelectItem>
                        {properties.map((p) => (
                          <SelectItem key={p.id} value={p.id}>
                            {p.label} — {p.city}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <p className="md:col-span-2 text-[11px] text-teal-700">
                    ✨ Les champs ci-dessous se remplissent automatiquement (nom, adresse, loyer du bail correspondant). Vous pouvez les ajuster librement.
                  </p>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
                {selected.fields.map((f) => (
                  <div key={f.name} className={f.type === "text" && f.name.includes("details") ? "md:col-span-2" : ""}>
                    <Label className="text-xs uppercase tracking-wider text-slate-600">
                      {f.label} {f.required && <span className="text-rose-500">*</span>}
                    </Label>
                    {f.type === "select" ? (
                      <Select value={values[f.name] || ""} onValueChange={(val) => setValues({ ...values, [f.name]: val })}>
                        <SelectTrigger data-testid={`letter-field-${f.name}`} className="mt-1">
                          <SelectValue placeholder="Choisir…" />
                        </SelectTrigger>
                        <SelectContent>
                          {(f.options || []).map((opt) => (
                            <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : f.type === "text" && (f.name.includes("details") || f.name.includes("modification") || f.name.includes("conditions") || f.name.includes("nature")) ? (
                      <Textarea
                        data-testid={`letter-field-${f.name}`}
                        value={values[f.name] || ""}
                        onChange={(e) => setValues({ ...values, [f.name]: e.target.value })}
                        rows={3}
                        className="mt-1"
                      />
                    ) : (
                      <Input
                        data-testid={`letter-field-${f.name}`}
                        type={f.type === "number" ? "number" : f.type === "date" ? "date" : "text"}
                        value={values[f.name] || ""}
                        onChange={(e) => setValues({ ...values, [f.name]: e.target.value })}
                        className="mt-1"
                      />
                    )}
                  </div>
                ))}
              </div>

              <DialogFooter className="mt-4">
                <Button variant="outline" onClick={() => setSelected(null)}>Annuler</Button>
                <Button
                  data-testid="letter-send-tenant-btn"
                  variant="outline"
                  onClick={sendToTenant}
                  disabled={sendingTenant || !pickTenant}
                  className="border-teal-500 text-teal-700 hover:bg-teal-50"
                >
                  <Mail className="h-4 w-4 mr-2" />
                  {sendingTenant ? "Envoi…" : "Envoyer au locataire"}
                </Button>
                <Button
                  data-testid="letter-generate-btn"
                  onClick={generate}
                  disabled={generating}
                  className="bg-[hsl(var(--primary))]"
                >
                  <Download className="h-4 w-4 mr-2" />
                  {generating ? "Génération…" : "Générer le PDF"}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
